// The AI team pages live in the main sidebar (AI team group); this wraps them.
// Chat and the workflow canvas stay full screen; closing the chat comes back
// to the page you left.
import { cn } from '@/lib/utils'
import {
  Activity,
  Brain,
  Briefcase,
  CalendarClock,
  Cpu,
  type LucideIcon,
  MessagesSquare,
  Plug
} from 'lucide-react'
import { useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

const SETTINGS_TABS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/apps/ai-team/jobs', label: 'Jobs', icon: Briefcase },
  { to: '/apps/ai-team/knows', label: 'What it knows', icon: Brain },
  { to: '/apps/ai-team/connections', label: 'Accounts', icon: Plug },
  { to: '/apps/ai-team/models', label: 'Models & pricing', icon: Cpu },
  { to: '/apps/ai-team/routines', label: 'Schedules', icon: CalendarClock },
  { to: '/apps/ai-team/activity', label: 'Activity', icon: Activity }
]

export function Workspace() {
  const { pathname, search } = useLocation()

  // Closing the chat brings you back to this page.
  useEffect(() => {
    try {
      sessionStorage.setItem('ai-team-return', pathname + search)
    } catch {
      // Private mode: closing falls back to the overview.
    }
  }, [pathname, search])

  // The pages are in the main sidebar (AI team group); bottom room keeps content clear of the Chief button.
  return (
    <div className="min-w-0 space-y-5 pb-28">
      <Outlet />
    </div>
  )
}

/** Settings pages share one tab row. */
export function SettingsLayout() {
  return (
    <div className="space-y-5">
      <nav aria-label="Settings" className="flex flex-wrap gap-1.5">
        {SETTINGS_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cn(
                'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors',
                isActive
                  ? 'bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300'
                  : 'bg-white text-neutral-600 hover:text-neutral-900 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:text-white'
              )
            }
          >
            <t.icon className="size-4" />
            {t.label}
          </NavLink>
        ))}
        {/* The full chat, for the rare times you want to talk it through. */}
        <Link
          to="/apps/ai-team/chat"
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
        >
          <MessagesSquare className="size-4" />
          Team room
        </Link>
      </nav>
      <Outlet />
    </div>
  )
}
